import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { 
  Clock, 
  Users, 
  CheckCircle, 
  FileText, 
  TrendingUp, 
  Mail, 
  Phone, 
  MapPin,
  ArrowRight,
  Briefcase,
  Shield,
  Target,
  Zap,
  Loader2
} from "lucide-react";
import { mockData } from "../mock/data";
import { toast } from "sonner";
import { Toaster } from "./ui/sonner";
import { demoRequestAPI } from "../services/api";
import { sendDemoRequestEmail } from "../services/email";
import { auth } from "../lib/firebase";
import { onAuthStateChanged } from "firebase/auth";
import { formatMarkdown } from "../lib/utils";

const HomePage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    firm: "",
    phone: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check Firebase Auth status
  useEffect(() => {
    // First, check if user is authenticated on current domain
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setIsAuthenticated(true);
        return;
      }
      
      // If not authenticated on current domain, check for cross-domain auth
      checkCrossDomainAuth();
    });

    // Also check immediately for current domain auth
    const currentUser = auth.currentUser;
    if (currentUser) {
      setIsAuthenticated(true);
    } else {
      checkCrossDomainAuth();
    }

    return () => unsubscribe();
  }, []);

  // Check for authentication across domains
  const checkCrossDomainAuth = async () => {
    try {
      // Method 1: Check for shared cookie (if app sets cookie with domain=.levellex.com)
      const cookies = document.cookie.split('; ').reduce((acc, cookie) => {
        const [key, value] = cookie.split('=');
        acc[key] = value;
        return acc;
      }, {});
      
      // Debug: Log all cookies to see what's available
      if (Object.keys(cookies).length > 0) {
        console.log('Available cookies:', Object.keys(cookies));
      } else {
        console.log('No cookies found. The app needs to set a shared cookie with domain=.levellex.com');
      }
      
      // Check for common auth cookie names
      const authCookieNames = ['firebase-auth', 'auth-token', 'authToken', 'user-auth', 'levellex-auth'];
      for (const cookieName of authCookieNames) {
        if (cookies[cookieName]) {
          console.log('✅ Auth cookie detected:', cookieName);
          setIsAuthenticated(true);
          return;
        }
      }
      
      console.log('❌ No auth cookie found. Cookie names checked:', authCookieNames);

      // Method 2: Try to fetch auth status from app domain
      // This requires the app to have an endpoint that returns auth status
      // Silently fail if endpoint doesn't exist or CORS is not configured
      const appUrl = getAppUrl();
      try {
        const response = await fetch(`${appUrl}/api/auth/status`, {
          method: 'GET',
          credentials: 'include', // Include cookies
          mode: 'cors'
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.authenticated) {
            setIsAuthenticated(true);
            return;
          }
        }
      } catch (error) {
        // Silently ignore CORS/network errors - this is expected if endpoint doesn't exist
        // No need to log this as it's a normal fallback attempt
      }

      // Method 3: Check localStorage for any Firebase-related keys (current domain only)
      // This won't work cross-domain but might catch auth on the same domain
      const firebaseKeys = Object.keys(localStorage).filter(key => 
        key.includes('firebase') && (key.includes('auth') || key.includes('user'))
      );
      
      if (firebaseKeys.length > 0) {
        // Try to validate one of them
        for (const key of firebaseKeys.slice(0, 1)) { // Just check first one
          try {
            const data = localStorage.getItem(key);
            if (data && (data.includes('uid') || data.includes('token'))) {
              const parsed = JSON.parse(data);
              if (parsed.uid || parsed.stsTokenManager) {
                setIsAuthenticated(true);
                return;
              }
            }
          } catch (e) {
            // Continue
          }
        }
      }
    } catch (error) {
      console.error('Error checking cross-domain auth:', error);
    }
  };

  const getAppUrl = () => {
    if (typeof window === "undefined") return "https://app.levellex.com";
    const host = window.location.hostname || "";

    if (host.endsWith("venturelex.ai")) {
      return "https://app.levellex.com";
    }

    if (host.endsWith("levellex.com")) {
      return "https://app.levellex.com";
    }

    return "https://app.levellex.com";
  };

  const handleFreeSignupClick = () => {
    window.open(getAppUrl(), "_blank");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Try to call the API first
      try {
        const response = await demoRequestAPI.create(formData);
        
        // Send email notification
        try {
          await sendDemoRequestEmail(formData);
          toast.success("Demo Request Submitted!", {
            description: "Your request has been submitted and an email confirmation has been sent. We'll contact you soon!",
            duration: 10000,
          });
        } catch (emailError) {
          console.error('Email notification failed:', emailError);
          // Still show success if API worked, but mention email may not have been sent
          toast.success("Demo Request Submitted!", {
            description: response.message || "Your request has been submitted. We'll contact you soon!",
            duration: 10000,
          });
        }
        
        // Reset form on success
        setFormData({ name: "", email: "", firm: "", phone: "" });
        return;
      } catch (apiError) {
        // If API fails (likely CORS), try EmailJS as fallback
        const errorMessage = apiError.message || "";
        const isCorsError = !apiError.response && (
          errorMessage.includes('temporarily unavailable') || 
          errorMessage.includes('pankaj@levellex.com') ||
          errorMessage.includes('Network Error') ||
          apiError.code === 'ERR_NETWORK'
        );
        
        if (isCorsError) {
          // Try EmailJS as fallback
          try {
            await sendDemoRequestEmail(formData);
            toast.success("Demo Request Submitted!", {
              description: "Your request has been sent successfully via email. We'll contact you soon!",
              duration: 10000,
            });
            
            // Reset form on success
            setFormData({ name: "", email: "", firm: "", phone: "" });
            return;
          } catch (emailError) {
            // If EmailJS also fails, show helpful message
            throw new Error('Please email us directly at pankaj@levellex.com with your details.');
          }
        } else {
          // Re-throw non-CORS errors
          throw apiError;
        }
      }
    } catch (error) {
      const errorMessage = error.message || "Please try again later.";
      const isCorsError = errorMessage.includes('pankaj@levellex.com') || errorMessage.includes('email us directly');
      
      toast.error(isCorsError ? "Form Submission Unavailable" : "Submission Failed", {
        description: errorMessage,
        duration: 10000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const scrollToDemo = (e) => {
    if (e) {
      e.preventDefault();
    }
    const demoSection = document.getElementById('demo');
    const demoForm = document.getElementById('demo-form');
    
    if (demoForm) {
      // If we're already in the demo section, scroll to the form
      demoForm.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else if (demoSection) {
      // Otherwise scroll to the demo section
      demoSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100">
      <Toaster />
      
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_redlineai/artifacts/vu85k9o1_1L%27s_Design1.png" 
                alt="LevelLex Logo" 
                className="w-10 h-10 object-contain"
              />
              <span className="text-2xl font-bold text-slate-900">LevelLex</span>
            </div>
            <nav className="hidden md:flex items-center space-x-6">
              <a href="#problem" className="text-slate-600 hover:text-slate-900 transition-colors">The Problem</a>
              <a href="#solution" className="text-slate-600 hover:text-slate-900 transition-colors">How It Works</a>
              <a href="#roi" className="text-slate-600 hover:text-slate-900 transition-colors">ROI</a>
              <Button 
                size="sm" 
                className="bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 font-semibold"
                onClick={scrollToDemo}
              >
                Book a "Proof of Quality"
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                className="border-slate-300 text-slate-700 hover:bg-slate-50 px-4 py-2 font-semibold"
                onClick={handleFreeSignupClick}
              >
                {isAuthenticated ? "Your Playbooks" : "Free Signup"}
              </Button>
            </nav>
            {/* Mobile Free Signup Button */}
            <Button 
              size="sm" 
              variant="outline"
              className="md:hidden border-slate-300 text-slate-700 hover:bg-slate-50 px-4 py-2 font-semibold"
              onClick={handleFreeSignupClick}
            >
              {isAuthenticated ? "Your Playbooks" : "Free Signup"}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-6">
        <div className="container mx-auto text-center max-w-4xl">
          <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-4 leading-tight">
            The Elastic Associate Team.
          </h1>
          <h2 className="text-3xl md:text-4xl font-semibold text-slate-700 mb-8">
            Scale your law firm instantly. We provide AI-augmented associates who review documents strictly against <em>your</em> firm's precedents.
          </h2>
          <p className="text-xl text-slate-600 mb-8 max-w-3xl mx-auto leading-relaxed">
            Zero Training Lag. 100% Deterministic. The "Maker-Checker" Advantage.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-4">
            <Button 
              size="lg" 
              className="bg-slate-900 hover:bg-slate-800 text-white px-10 py-4 text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
              onClick={scrollToDemo}
            >
              Book a "Proof of Quality" Pilot <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="border-slate-300 text-slate-700 hover:bg-slate-50 px-10 py-4 text-lg font-semibold"
              onClick={() => document.getElementById('solution').scrollIntoView({ behavior: 'smooth' })}
            >
              How it Works
            </Button>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section id="problem" className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-6">
              The "Partner Bottleneck"
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              You have the deal flow, but you don't have the capacity.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {mockData.problems.map((problem, index) => (
              <Card key={index} className="border-slate-200 hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                    <problem.icon className="w-6 h-6 text-red-600" />
                  </div>
                  <CardTitle className="text-slate-900">{problem.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 mb-4">{formatMarkdown(problem.description)}</p>
                  <div className="text-sm font-semibold text-red-600 uppercase tracking-wide">{problem.impact}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section id="solution" className="py-20 px-6 bg-slate-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-6">
              Not Just "Outsourcing." Precision Engineering.
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-8">
              We don't just throw bodies at the problem. We use our proprietary <strong>LevelLex Intelligence Engine</strong> to turn your historical documents into a strict set of guardrails for our associates.
            </p>
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-slate-200 max-w-4xl mx-auto mb-8">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">The "Maker-Checker" Model:</h3>
              <p className="text-lg text-slate-700 mb-4">
                In high-stakes legal work, AI should never be the final sign-off.
              </p>
              <div className="grid md:grid-cols-2 gap-6 text-left">
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="font-semibold text-slate-900 mb-2">The Maker (Human):</p>
                  <p className="text-slate-600">Our vetted associates draft, redline, and reason.</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="font-semibold text-slate-900 mb-2">The Checker (LevelLex AI):</p>
                  <p className="text-slate-600">Our engine instantly validates every position against your firm's history, preventing deviations and hallucinations.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="space-y-6">
                {mockData.howItWorks.map((step, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-slate-900 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                      {index + 1}
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-2">{step.title}</h4>
                      <p className="text-slate-600">{formatMarkdown(step.description)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-white p-4 rounded-2xl shadow-lg border border-slate-200">
              <img 
                src="https://storage.googleapis.com/levellex-website/levellex solution.png" 
                alt="LevelLex Solution" 
                className="w-full h-auto rounded-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-6">
              From "Overwhelmed" to "Approved" — Without New Software
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-4">
              <em>You don't need to learn a new tool. We deliver work in your native environment.</em>
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {mockData.processSteps.map((step, index) => (
              <Card key={index} className="border-slate-200 hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-slate-900 text-white rounded-full flex items-center justify-center mb-4 mx-auto">
                    <span className="text-xl font-bold">{index + 1}</span>
                  </div>
                  <CardTitle className="text-slate-900 text-center">{step.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 text-center">{formatMarkdown(step.description)}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-20 px-6 bg-slate-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-6">
              Why LevelLex Associates Outperform
            </h2>
          </div>
          
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Feature</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-slate-600">Traditional LPO / Upwork</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-slate-600">Generic AI (ChatGPT)</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-slate-900 bg-green-50">LevelLex Team</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {mockData.comparison.features.map((feature, index) => (
                    <tr key={index} className="hover:bg-slate-50">
                      <td className="px-6 py-4 text-sm font-medium text-slate-900">{feature.feature}</td>
                      <td className="px-6 py-4 text-sm text-center text-slate-600">{feature.genericLPO}</td>
                      <td className="px-6 py-4 text-sm text-center text-slate-600">{feature.genericAI}</td>
                      <td className="px-6 py-4 text-sm text-center font-semibold text-slate-900 bg-green-50">
                        {feature.levellex}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Sample Output Section */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-6">
              What You Get Back
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              <em>You don't just get a redline. You get certainty.</em>
            </p>
          </div>

          <Card className="border-slate-200 shadow-lg">
            <CardHeader>
              <CardTitle className="text-slate-900">{mockData.documentAnalysis[0].clause}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Badge className="bg-red-100 text-red-800">🔴 Deviation Fixed</Badge>
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-700 mb-1">Action:</p>
                <p className="text-slate-600">{mockData.documentAnalysis[0].action}</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-700 mb-1">Precedent:</p>
                <p className="text-slate-600 italic">Matches position taken in <strong>{mockData.documentAnalysis[0].source}</strong> and <strong>Beta Inc MSA (2024)</strong>.</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <p className="text-sm font-semibold text-slate-700 mb-1">Associate Note:</p>
                <p className="text-slate-600 text-sm">"{mockData.documentAnalysis[0].note}"</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Trust & Security Section */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-6">
              Your Data. Your Rules.
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-8">
              We solve the "Confidentiality" deadlock.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-slate-200 hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-blue-600" />
                </div>
                <CardTitle className="text-slate-900">Client-Grade Isolation</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600">Your data is processed in isolated environments.</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200 hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-green-600" />
                </div>
                <CardTitle className="text-slate-900">No Public Training</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600">Your documents are <strong>never</strong> used to train public models like ChatGPT.</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200 hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-purple-600" />
                </div>
                <CardTitle className="text-slate-900">Privileged Extension</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600">Our associates operate under strict NDAs, functioning as a direct extension of your firm.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="roi" className="py-20 px-6 bg-slate-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-6">
              Measure the Impact
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {mockData.benefits.map((benefit, index) => (
              <Card key={index} className="text-center border-slate-200 hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <benefit.icon className="w-8 h-8 text-slate-700" />
                  </div>
                  <CardTitle className="text-3xl font-bold text-slate-900 mb-2">{benefit.metric}</CardTitle>
                  <CardDescription className="text-slate-600 text-base">{formatMarkdown(benefit.description)}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Demo Request Form */}
      <section id="demo" className="py-20 px-6 bg-slate-900">
        <div className="container mx-auto max-w-2xl text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Stop Doing "Hygiene" Work.
          </h2>
          <p className="text-xl text-slate-300 mb-4">
            Focus on the strategy. Let LevelLex handle the execution.
          </p>
          <p className="text-lg text-slate-400 mb-8">
            <strong className="text-white">Book a "Proof of Quality" Pilot</strong><br />
            Send us one document and your standard. We will show you the result for free.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
            <Button 
              size="lg" 
              className="bg-white hover:bg-slate-100 text-slate-900 px-8 py-4 text-lg font-semibold shadow-lg"
              onClick={scrollToDemo}
            >
              Schedule a Call
            </Button>
          </div>
          <p className="text-slate-400 mb-8">
            Email us at <a href="mailto:pankaj@levellex.com" className="text-white hover:underline">pankaj@levellex.com</a>
          </p>
          
          <div id="demo-form">
            <Card className="border-slate-700 bg-slate-800">
              <CardHeader>
                <CardTitle className="text-white">Book a "Proof of Quality" Pilot</CardTitle>
                <CardDescription className="text-slate-400">
                  Send us one document and your standard. We will show you the result for free.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <Input
                      name="name"
                      placeholder="Your Name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="bg-slate-700 border-slate-600 text-white placeholder-slate-400"
                    />
                    <Input
                      name="email"
                      type="email"
                      placeholder="Email Address"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="bg-slate-700 border-slate-600 text-white placeholder-slate-400"
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <Input
                      name="firm"
                      placeholder="Law Firm Name"
                      value={formData.firm}
                      onChange={handleInputChange}
                      required
                      className="bg-slate-700 border-slate-600 text-white placeholder-slate-400"
                    />
                    <Input
                      name="phone"
                      placeholder="Phone Number"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="bg-slate-700 border-slate-600 text-white placeholder-slate-400"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    size="lg" 
                    className="w-full bg-white text-slate-900 hover:bg-slate-100 py-4 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        Schedule Demo <ArrowRight className="ml-2 w-5 h-5" />
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 px-6 bg-white border-t border-slate-200">
        <div className="container mx-auto max-w-4xl">
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-2xl font-bold text-slate-900 mb-6">Get in Touch</h3>
              <p className="text-slate-600 mb-6">
                Questions about how LevelLex can enhance your practice? We're here to help you explore the possibilities.
              </p>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-slate-600" />
                  <a href="mailto:pankaj@levellex.com" className="text-slate-900 hover:text-slate-700 hover:underline">pankaj@levellex.com</a>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-slate-600" />
                  <a 
                    href="#demo" 
                    onClick={scrollToDemo}
                    className="text-slate-900 hover:text-slate-700 hover:underline cursor-pointer"
                  >
                    Schedule a call
                  </a>
                </div>
                <div className="flex items-center space-x-3">
                  <MapPin className="w-5 h-5 text-slate-600" />
                  <span className="text-slate-900">Serving law firms globally</span>
                </div>
              </div>
            </div>
              <div className="bg-slate-50 p-6 rounded-lg">
              <h4 className="font-semibold text-slate-900 mb-3">Trust Badges</h4>
              <ul className="space-y-2 text-slate-600">
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-600 mr-2 flex-shrink-0" />
                  Enterprise-grade security
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-600 mr-2 flex-shrink-0" />
                  Client-Grade Isolation
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-600 mr-2 flex-shrink-0" />
                  100% Auditable
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-600 mr-2 flex-shrink-0" />
                  Full Logic Log
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default HomePage;